import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Store, Product } from '../types';
import { Store as StoreIcon, Package, Search, Plus, Pencil, Trash2, Upload, X, ChevronLeft, Tag, Barcode, ChevronDown, ArrowUpDown } from 'lucide-react';
import { getApiBase } from '../utils/apiBase';

interface ProductsProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
}

const s = {
  page: { padding: '32px', background: '#f8f7f5', minHeight: '100vh', fontFamily: "'DM Sans', sans-serif" } as React.CSSProperties,
  inp: { width: '100%', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '9px 12px', fontSize: '14px', color: '#111827', outline: 'none', boxSizing: 'border-box' as const },
  label: { fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '4px', display: 'block' },
  overlay: { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' },
  modal: { background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '420px' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 0' },
  modalTitle: { margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' },
  modalBody: { padding: '20px 24px', display: 'flex', flexDirection: 'column' as const, gap: '12px' },
  modalFooter: { padding: '0 24px 20px', display: 'flex', gap: '8px', justifyContent: 'flex-end' },
  cancelBtn: { padding: '9px 18px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', fontSize: '14px', fontWeight: 500, color: '#374151', cursor: 'pointer' },
  saveBtn: { padding: '9px 18px', borderRadius: '8px', border: 'none', background: '#F1561D', color: '#fff', fontSize: '14px', fontWeight: 500, cursor: 'pointer' },
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div><span style={s.label}>{label}</span>{children}</div>
);

const StoreLogo: React.FC<{ logo?: string | null; name: string }> = ({ logo, name }) => {
  const [errored, setErrored] = useState(false);
  useEffect(() => { setErrored(false); }, [logo]);

  if (logo && !errored) {
    return (
      <img
        src={logo}
        alt={`${name} logo`}
        onError={() => setErrored(true)}
        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' }}
      />
    );
  }
  return <Package size={20} color="#F1561D" />;
};

const Products: React.FC<ProductsProps> = ({ searchTerm, setSearchTerm }) => {
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingStores, setLoadingStores] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [newProduct, setNewProduct] = useState({ name: '', category: '', price: '', barcode: '' });
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<{ inserted: number; skipped: number; skippedBarcodes: string[] } | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [sortAlpha, setSortAlpha] = useState(false);

  const API_BASE = getApiBase();
  const token = localStorage.getItem('authToken');
  const authHeader = { Authorization: `Bearer ${token}` };

  /* -----------------------------------------------------------
     FETCH CURRENT USER
  ------------------------------------------------------------ */
  const fetchCurrentUser = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/auth/me`, { headers: authHeader });
      const user = res.data.user ?? res.data;
      setCurrentUser(user);
      return user;
    } catch { return null; }
  };

  /* -----------------------------------------------------------
     FETCH STORES
  ------------------------------------------------------------ */
  useEffect(() => {
    const fetchStores = async () => {
      try {
        setLoadingStores(true);
        const user = await fetchCurrentUser();
        const res = await axios.get(`${API_BASE}/api/stores`, { headers: authHeader });
        const storesArray = Array.isArray(res.data.data) ? res.data.data : [];
        if (user?.role?.toLowerCase() === 'manager' && user?.storeId) {
          const ms = storesArray.find((s: Store) => s._id === user.storeId);
          if (ms) { setStores([ms]); setSelectedStore(ms); } else setStores([]);
        } else {
          setStores(storesArray);
        }
      } catch { setStores([]); }
      finally { setLoadingStores(false); }
    };
    fetchStores();
  }, [API_BASE]);

  /* -----------------------------------------------------------
     FETCH PRODUCTS
  ------------------------------------------------------------ */
  useEffect(() => {
    if (!selectedStore) return;
    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const res = await axios.get(
          `${API_BASE}/api/products?storeId=${selectedStore._id}`,
          { headers: authHeader }
        );
        setProducts(Array.isArray(res.data.data) ? res.data.data : []);
      } catch { setProducts([]); }
      finally { setLoadingProducts(false); }
    };
    fetchProducts();
  }, [API_BASE, selectedStore]);

  /* -----------------------------------------------------------
     ADD PRODUCT
  ------------------------------------------------------------ */
  const addProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStore) return;
    setSaving(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/products`,
        {
          name: newProduct.name,
          category: newProduct.category,
          price: Number(newProduct.price),
          barcode: newProduct.barcode,
          stock: 0,
          storeId: selectedStore._id,
        },
        { headers: authHeader }
      );
      if (res.data) {
        setProducts(prev => [res.data, ...prev]);
        setShowAddModal(false);
        setNewProduct({ name: '', category: '', price: '', barcode: '' });
      }
    } catch (err: any) {
      alert(err.response?.data?.message || err.response?.data?.error || 'Failed to add product');
    } finally { setSaving(false); }
  };

  /* -----------------------------------------------------------
     UPDATE PRODUCT
  ------------------------------------------------------------ */
  const updateProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editProduct) return;
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_BASE}/api/products/${editProduct._id}`,
        {
          name: editProduct.name,
          category: editProduct.category,
          price: Number(editProduct.price),
          barcode: editProduct.barcode,
        },
        { headers: authHeader }
      );
      if (res.data) {
        setProducts(prev => prev.map(p => p._id === res.data._id ? { ...res.data } : p));
        setShowEditModal(false);
        setEditProduct(null);
      }
    } catch (err: any) {
      alert(err.response?.data?.message || err.response?.data?.error || 'Failed to update product');
    } finally { setSaving(false); }
  };

  /* -----------------------------------------------------------
     DELETE PRODUCT
  ------------------------------------------------------------ */
  const deleteProduct = async (id: string) => {
    if (!window.confirm('Delete this product?')) return;
    setDeleting(id);
    try {
      await axios.delete(`${API_BASE}/api/products/${id}`, { headers: authHeader });
      setProducts(prev => prev.filter(p => p._id !== id));
    } catch (err: any) {
      alert(err.response?.data?.message || err.response?.data?.error || 'Failed to delete product');
    } finally { setDeleting(null); }
  };

  /* -----------------------------------------------------------
     CSV UPLOAD
  ------------------------------------------------------------ */
  const handleCsvUpload = async () => {
    if (!csvFile || !selectedStore) { alert('Select a store and a CSV file first'); return; }

    const rawText = await csvFile.text();
    const cleanText = rawText
      .replace(/^\uFEFF/, '')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n');
    const lines = cleanText.split('\n').filter(l => l.trim() !== '');
    if (lines.length < 2) { alert('Upload failed. The file appears to be empty.'); return; }

    const firstLine = lines[0];
    const separator = firstLine.includes('\t') ? '\t' : ',';
    const headers = firstLine.split(separator).map(h => h.trim().toLowerCase());
    const required = ['name', 'category', 'price', 'barcode'];
    const missing = required.filter(h => !headers.includes(h));
    if (missing.length > 0) {
      alert(`Upload failed.\n\nMissing columns: ${missing.join(', ')}\n\nFound: ${headers.join(', ')}\n\nCheck that headers are lowercase with no extra spaces.`);
      return;
    }

    const formData = new FormData();
    formData.append('file', csvFile);
    formData.append('storeId', selectedStore._id);

    try {
      setUploading(true);
      const res = await axios.post(`${API_BASE}/api/products/bulk-upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.data) {
        const { inserted, skipped, skippedBarcodes } = res.data;
        setProducts(prev => [...(res.data.products || []), ...prev]);
        setCsvFile(null);
        setUploadResult({ inserted, skipped, skippedBarcodes });
      }
    } catch (err: any) {
      alert(err.response?.data?.message || err.response?.data?.error || 'CSV upload failed');
    } finally { setUploading(false); }
  };

  /* -----------------------------------------------------------
     DERIVED STATE
  ------------------------------------------------------------ */
  const categories = Array.from(new Set(products.map(p => p.category).filter(Boolean))).sort();

  const filtered = products
    .filter(p =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (selectedCategory === '' || p.category === selectedCategory)
    )
    .sort((a, b) => sortAlpha ? a.name.localeCompare(b.name) : 0);

  const clearFilters = () => { setSelectedCategory(''); setSortAlpha(false); };

  /* -----------------------------------------------------------
     RENDER
  ------------------------------------------------------------ */
  if (loadingStores) {
    return <div style={{ padding: '32px', fontFamily: 'DM Sans, sans-serif', color: '#9ca3af' }}>Loading stores...</div>;
  }

  if (currentUser?.role?.toLowerCase() === 'manager' && !currentUser?.storeId) {
    return (
      <div style={{ ...s.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '32px', textAlign: 'center', maxWidth: '400px' }}>
          <p style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#92400e' }}>No Store Assigned</p>
          <p style={{ margin: '8px 0 0', fontSize: '14px', color: '#b45309' }}>Contact an administrator to assign you to a store.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      {/* ── Store picker ──────────────────────────────────────────────── */}
      {!selectedStore ? (
        <>
          <div style={{ marginBottom: '28px' }}>
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 600, color: '#111827' }}>Products</h1>
            <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#9ca3af' }}>Select a store to manage its catalog</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px' }}>
            {stores.map(store => (
              <div key={store._id}
                onClick={() => setSelectedStore(store)}
                style={{ background: '#fff', borderRadius: '14px', border: '1px solid #f3f4f6', padding: '24px', cursor: 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s' }}
                onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = '#F1561D'; (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 20px rgba(241,86,29,0.1)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = '#f3f4f6'; (e.currentTarget as HTMLDivElement).style.boxShadow = 'none'; }}>

                {/* ── Logo or fallback ── */}
                <div style={{
                  width: '44px', height: '44px', borderRadius: '12px',
                  background: (store as any).logo ? 'transparent' : '#fff5f2',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: '14px', overflow: 'hidden', border: (store as any).logo ? '1px solid #f3f4f6' : 'none',
                }}>
                  <StoreLogo logo={(store as any).logo} name={store.name} />
                </div>

                <p style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>{store.name}</p>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#9ca3af' }}>{store.description || 'No description'}</p>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          {/* ── Catalog header ──────────────────────────────────────────── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {currentUser?.role?.toLowerCase() !== 'manager' && (
                <button
                  onClick={() => { setSelectedStore(null); setSelectedCategory(''); setSortAlpha(false); }}
                  style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
                  <ChevronLeft size={16} /> Back
                </button>
              )}
              <div>
                <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#111827' }}>{selectedStore.name}</h1>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#9ca3af' }}>{products.length} product{products.length !== 1 ? 's' : ''}</p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 14px', fontSize: '13px', color: '#374151', cursor: 'pointer', fontWeight: 500 }}>
                <Upload size={14} />
                {csvFile ? csvFile.name.slice(0, 16) + '…' : 'Import CSV'}
                <input type="file" accept=".csv" style={{ display: 'none' }} onChange={e => setCsvFile(e.target.files?.[0] ?? null)} />
              </label>
              {csvFile && (
                <button onClick={handleCsvUpload} disabled={uploading}
                  style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 14px', fontSize: '13px', fontWeight: 500, cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.6 : 1 }}>
                  {uploading ? 'Uploading…' : 'Upload'}
                </button>
              )}
              <button onClick={() => setShowAddModal(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#F1561D', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}>
                <Plus size={16} /> Add Product
              </button>
            </div>
          </div>

          {/* ── Search ──────────────────────────────────────────────────── */}
          <div style={{ position: 'relative', marginBottom: '16px' }}>
            <Search size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input
              type="text"
              placeholder="Search products…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ ...s.inp, paddingLeft: '40px' }}
            />
          </div>

          {/* ── Filters ─────────────────────────────────────────────────── */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <select
                value={selectedCategory}
                onChange={e => setSelectedCategory(e.target.value)}
                style={{
                  ...s.inp, width: 'auto', paddingRight: '32px',
                  appearance: 'none', WebkitAppearance: 'none',
                  background: selectedCategory ? '#fff5f2' : '#fff',
                  borderColor: selectedCategory ? '#F1561D' : '#e5e7eb',
                  color: selectedCategory ? '#F1561D' : '#374151',
                  fontWeight: selectedCategory ? 600 : 400,
                  cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                }}>
                <option value=''>All Categories</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <ChevronDown size={14} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: selectedCategory ? '#F1561D' : '#9ca3af', pointerEvents: 'none' }} />
            </div>

            <button
              onClick={() => setSortAlpha(prev => !prev)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '9px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
                cursor: 'pointer', transition: 'all 0.15s',
                border: sortAlpha ? '1px solid #F1561D' : '1px solid #e5e7eb',
                background: sortAlpha ? '#fff5f2' : '#fff',
                color: sortAlpha ? '#F1561D' : '#374151',
                fontFamily: "'DM Sans', sans-serif",
              }}>
              <ArrowUpDown size={14} /> A–Z
            </button>

            {(selectedCategory || sortAlpha) && (
              <button onClick={clearFilters}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  padding: '7px 12px', borderRadius: '8px', fontSize: '12px',
                  border: '1px solid #fee2e2', background: '#fff5f5',
                  color: '#dc2626', cursor: 'pointer', fontWeight: 500,
                  fontFamily: "'DM Sans', sans-serif",
                }}>
                <X size={12} /> Clear filters
              </button>
            )}

            <span style={{ fontSize: '13px', color: '#9ca3af', marginLeft: 'auto' }}>
              {filtered.length} of {products.length} product{products.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* ── Product grid ─────────────────────────────────────────────── */}
          {loadingProducts ? (
            <p style={{ color: '#9ca3af', fontSize: '14px' }}>Loading products…</p>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af' }}>
              <Package size={36} style={{ margin: '0 auto 12px', display: 'block', opacity: 0.4 }} />
              <p style={{ margin: 0 }}>No products found</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px' }}>
              {filtered.map(product => {
                const revenue = (product.price ?? 0) * (product.sales ?? 0);
                return (
                  <div key={product._id}
                    style={{ background: '#fff', borderRadius: '14px', border: '1px solid #f3f4f6', overflow: 'hidden', transition: 'box-shadow 0.15s' }}
                    onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 20px rgba(0,0,0,0.07)'}
                    onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.boxShadow = 'none'}>

                    <div style={{ padding: '18px 18px 14px', borderBottom: '1px solid #f9fafb' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Package size={20} color="#9ca3af" />
                        </div>
                        <span style={{ background: '#fff5f2', color: '#ea580c', fontSize: '11px', fontWeight: 600, padding: '4px 10px', borderRadius: '20px' }}>
                          {product.sales ?? 0} sold
                        </span>
                      </div>
                      <p style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#111827', lineHeight: 1.3 }}>{product.name}</p>
                    </div>

                    <div style={{ padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280' }}>
                        <Tag size={12} color="#d1d5db" /> {product.category}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280', fontFamily: 'monospace' }}>
                        <Barcode size={12} color="#d1d5db" /> {product.barcode || '—'}
                      </div>
                    </div>

                    <div style={{ padding: '12px 18px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <p style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#111827' }}>
                          R {typeof product.price === 'number' ? product.price.toFixed(2) : product.price}
                        </p>
                        {(product.sales ?? 0) > 0 && (
                          <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#16a34a' }}>R {revenue.toFixed(2)} revenue</p>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => { setEditProduct(product); setShowEditModal(true); }}
                          style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '7px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', fontSize: '12px', fontWeight: 500, color: '#374151', cursor: 'pointer' }}>
                          <Pencil size={12} /> Edit
                        </button>
                        <button onClick={() => deleteProduct(product._id)} disabled={deleting === product._id}
                          style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '7px 12px', borderRadius: '8px', border: '1px solid #fee2e2', background: '#fff5f5', fontSize: '12px', fontWeight: 500, color: '#dc2626', cursor: deleting === product._id ? 'not-allowed' : 'pointer', opacity: deleting === product._id ? 0.6 : 1, minWidth: '74px', justifyContent: 'center' }}>
                          {deleting === product._id ? 'Deleting…' : <><Trash2 size={12} /> Delete</>}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── Add Modal ─────────────────────────────────────────────────── */}
      {showAddModal && (
        <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) setShowAddModal(false); }}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Add Product</h3>
              <button onClick={() => setShowAddModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}><X size={20} /></button>
            </div>
            <form onSubmit={addProduct}>
              <div style={s.modalBody}>
                <Field label="Product Name"><input style={s.inp} placeholder="e.g. Apple iPhone 15" value={newProduct.name} onChange={e => setNewProduct({ ...newProduct, name: e.target.value })} required /></Field>
                <Field label="Category"><input style={s.inp} placeholder="e.g. Electronics" value={newProduct.category} onChange={e => setNewProduct({ ...newProduct, category: e.target.value })} required /></Field>
                <Field label="Price (R)"><input style={s.inp} type="number" placeholder="0.00" step="0.01" value={newProduct.price} onChange={e => setNewProduct({ ...newProduct, price: e.target.value })} required /></Field>
                <Field label="Barcode"><input style={s.inp} placeholder="Scan or type barcode" value={newProduct.barcode} onChange={e => setNewProduct({ ...newProduct, barcode: e.target.value })} /></Field>
              </div>
              <div style={s.modalFooter}>
                <button type="button" style={s.cancelBtn} onClick={() => setShowAddModal(false)} disabled={saving}>Cancel</button>
                <button type="submit" disabled={saving} style={{ ...s.saveBtn, opacity: saving ? 0.7 : 1, cursor: saving ? 'not-allowed' : 'pointer', minWidth: '110px' }}>
                  {saving ? 'Adding…' : 'Add Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Upload Result Modal ──────────────────────────────────────────── */}
      {uploadResult && (
        <div style={s.overlay} onClick={() => setUploadResult(null)}>
          <div style={{ ...s.modal, maxWidth: '460px' }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Upload Complete</h3>
              <button onClick={() => setUploadResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}><X size={20} /></button>
            </div>
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', background: '#f0fdf4', borderRadius: '10px', border: '1px solid #bbf7d0' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: '#fff', fontSize: '18px', fontWeight: 700, lineHeight: 1 }}>+</span>
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#065f46' }}>
                    {uploadResult.inserted} product{uploadResult.inserted !== 1 ? 's' : ''} added
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#16a34a' }}>Successfully saved to the catalog</p>
                </div>
              </div>
              {uploadResult.skipped > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '14px 16px', background: '#fffbeb', borderRadius: '10px', border: '1px solid #fde68a' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ color: '#fff', fontSize: '18px', fontWeight: 700, lineHeight: 1 }}>!</span>
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#92400e' }}>
                        {uploadResult.skipped} skipped (duplicate barcode{uploadResult.skipped !== 1 ? 's' : ''})
                      </p>
                      <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#b45309' }}>These barcodes already exist in this store</p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                    {uploadResult.skippedBarcodes.map(b => (
                      <span key={b} style={{ fontSize: '11px', fontFamily: 'monospace', background: '#fef3c7', color: '#92400e', padding: '3px 8px', borderRadius: '6px', border: '1px solid #fde68a' }}>
                        {b}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div style={s.modalFooter}>
              <button style={s.saveBtn} onClick={() => setUploadResult(null)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Modal ────────────────────────────────────────────────── */}
      {showEditModal && editProduct && (
        <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) { setShowEditModal(false); setEditProduct(null); } }}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Edit Product</h3>
              <button onClick={() => { setShowEditModal(false); setEditProduct(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}><X size={20} /></button>
            </div>
            <form onSubmit={updateProduct}>
              <div style={s.modalBody}>
                <Field label="Product Name"><input style={s.inp} value={editProduct.name} onChange={e => setEditProduct({ ...editProduct, name: e.target.value })} required /></Field>
                <Field label="Category"><input style={s.inp} value={editProduct.category} onChange={e => setEditProduct({ ...editProduct, category: e.target.value })} required /></Field>
                <Field label="Price (R)"><input style={s.inp} type="number" step="0.01" value={editProduct.price} onChange={e => setEditProduct({ ...editProduct, price: Number(e.target.value) })} required /></Field>
                <Field label="Barcode"><input style={s.inp} value={editProduct.barcode} onChange={e => setEditProduct({ ...editProduct, barcode: e.target.value })} /></Field>
              </div>
              <div style={s.modalFooter}>
                <button type="button" style={s.cancelBtn} onClick={() => { setShowEditModal(false); setEditProduct(null); }} disabled={saving}>Cancel</button>
                <button type="submit" disabled={saving} style={{ ...s.saveBtn, opacity: saving ? 0.7 : 1, cursor: saving ? 'not-allowed' : 'pointer', minWidth: '120px' }}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
